import React from 'react';
import graduationCapIcon from '../../../../assets/img/graduation_cap.png';

const getInitials = (name: string): string => {
    if (!name) return '';
    const nameParts = name.split(' ');
    if (nameParts.length === 1) {
        return nameParts[0].charAt(0).toUpperCase();
    }
    const firstInitial = nameParts[0].charAt(0);
    const lastInitial = nameParts[nameParts.length - 1].charAt(0);
    return `${firstInitial}${lastInitial}`.toUpperCase();
};

interface CollaboratorAvatarProps {
    photoUrl?: string | null;
    name: string;
    size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';
    activityStatus?: string;
    isRaroAcademyAlumnus?: boolean;
}

const sizeMap = {
    xsmall: 'h-6 w-6',
    small: 'h-8 w-8',
    medium: 'h-10 w-10',
    large: 'h-16 w-16',
    xlarge: 'h-24 w-24',
};

const decorationSizeMap = {
    xsmall: 'h-2 w-2',
    small: 'h-2 w-2',
    medium: 'h-2.5 w-2.5',
    large: 'h-3 w-3',
    xlarge: 'h-4 w-4',
};

const graduationCapSizeMap = {
    xsmall: 'w-5 h-5',
    small: 'w-6 h-6',
    medium: 'w-8 h-8',
    large: 'w-10 h-10',
    xlarge: 'w-12 h-12',
};

const textSizeMap = {
    xsmall: 'text-xs',
    small: 'text-xs',
    medium: 'text-sm',
    large: 'text-lg',
    xlarge: 'text-2xl',
};

const CollaboratorAvatar: React.FC<CollaboratorAvatarProps> = ({
    photoUrl,
    name,
    size = 'medium',
    activityStatus,
    isRaroAcademyAlumnus
}) => {
    const sizeClass = sizeMap[size];
    const textSizeClass = textSizeMap[size];
    const decorationSizeClass = decorationSizeMap[size];
    const graduationCapSizeClass = graduationCapSizeMap[size];

    // Determine activity status color
    const getActivityStatusColor = () => {
        if (!activityStatus) return 'bg-gray-400';
        const lowerStatus = activityStatus.toLowerCase();
        switch (lowerStatus) {
            case 'ativo':
                return 'bg-green-400';
            case 'desligado':
                return 'bg-red-400';
            case 'inativo':
                return 'bg-yellow-400';
            default:
                return 'bg-gray-400';
        }
    };

    return (
        <div className={`relative flex-shrink-0 ${sizeClass}`}>
            {/* Avatar principal */}
            <div className="h-full w-full">
                {photoUrl ? (
                    <img
                        className="h-full w-full rounded-full object-cover"
                        src={photoUrl}
                        alt={name}
                        referrerPolicy="no-referrer"
                    />
                ) : (
                    <span className="inline-flex h-full w-full items-center justify-center rounded-full bg-gray-500">
                        <span className={`font-medium leading-none text-white ${textSizeClass}`}>{getInitials(name)}</span>
                    </span>
                )}
            </div>

            {/* Indicador de Status de Atividade - Esquerda */}
            {activityStatus && (
                <span
                    className={`absolute bottom-0 left-0 block ${decorationSizeClass} rounded-full ring-2 ring-white ${getActivityStatusColor()}`}
                    title={`Status: ${activityStatus}`}
                />
            )}

            {/* Chapéu Acadêmico - Bem acima do avatar */}
            {isRaroAcademyAlumnus && (
                <div
                    className="absolute -top-5 left-1/2 transform -translate-x-1/2 w-10 h-10 pointer-events-none z-10"
                    title="Ex-Raro Academy"
                >
                    <img
                        src={graduationCapIcon}
                        alt="Graduation Cap"
                        className="w-full h-full object-contain drop-shadow-sm"
                    />
                </div>
            )}
        </div>
    );
};

export default CollaboratorAvatar; 